import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  BarChart3,
  Search,
  Loader2,
  AlertTriangle,
  Database,
  Hash,
  Layers,
  Home,
  Upload,
} from 'lucide-react';

import { cn } from '../lib/utils';
import {
  AnalysisStats,
  CodeProfile,
  RoomTemplate,
  RoomTypeInfo,
} from '../types';
import { BackendAPI } from '../lib/api';

import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import CodeLookupTab from '../components/AnalysisCodeLookup';
import AdminUploadTab from '../components/AdminUploadTab';

// ================================================================= //
//          Analysis Dashboard Page                                    //
// ================================================================= //
// Admin page showing the analysis engine's learned patterns:          //
// - Corpus statistics                                                 //
// - Top codes table                                                   //
// - Room templates browser                                            //
// - Code lookup                                                       //
// ================================================================= //

type ActiveTab = 'overview' | 'codes' | 'rooms' | 'upload' | 'lookup';

export default function AnalysisDashboardPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [stats, setStats] = useState<AnalysisStats | null>(null);
  const [topCodes, setTopCodes] = useState<CodeProfile[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomTypeInfo[]>([]);
  const [totalCodes, setTotalCodes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Reason: Auto-refresh overview stats every 30s so admin sees live updates
  useEffect(() => {
    if (activeTab !== 'overview') return;
    const interval = setInterval(() => {
      loadData();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsResult, codesResult, roomsResult] = await Promise.all([
        BackendAPI.getAnalysisStats(),
        BackendAPI.getTopCodes(20),
        BackendAPI.getRoomTypes(),
      ]);
      setStats(statsResult);
      setTopCodes(codesResult.codes);
      setTotalCodes(codesResult.total_codes);
      setRoomTypes(roomsResult.room_types);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analysis data');
    } finally {
      setLoading(false);
    }
  };

  const tabs: { id: ActiveTab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'codes', label: 'Top Codes', icon: Hash },
    { id: 'rooms', label: 'Room Templates', icon: Home },
    { id: 'upload', label: 'Upload', icon: Upload },
    { id: 'lookup', label: 'Code Lookup', icon: Search },
  ];

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4 text-lg text-muted-foreground">Loading analysis data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <AlertTriangle className="h-12 w-12" />
        <h2 className="mt-4 text-xl font-semibold">Error Loading Data</h2>
        <p>{error}</p>
        <Button onClick={loadData} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm font-medium text-muted-foreground">
        <Link to="/" className="hover:text-primary">Dashboard</Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="text-foreground">Analysis Engine</span>
      </nav>

      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary" />
          Analysis Dashboard
        </h2>
        <p className="text-muted-foreground">
          Manage and explore the Xactimate knowledge base.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'group inline-flex items-center border-b-2 py-3 px-1 text-sm font-medium',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-gray-300 dark:hover:border-border hover:text-foreground'
              )}
            >
              <tab.icon className="-ml-0.5 mr-2 h-5 w-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && stats && <OverviewTab stats={stats} />}
      {activeTab === 'codes' && <TopCodesTab codes={topCodes} />}
      {activeTab === 'rooms' && <RoomTemplatesTab roomTypes={roomTypes} />}
      {activeTab === 'upload' && <AdminUploadTab onProcessingComplete={loadData} />}
      {activeTab === 'lookup' && <CodeLookupTab />}
    </div>
  );
}

// ================================================================= //
//          Overview Tab                                                //
// ================================================================= //

const OverviewTab: React.FC<{ stats: AnalysisStats }> = ({ stats }) => {
  const statCards = [
    { label: 'Total Estimates', value: stats.total_estimates, icon: Database },
    { label: 'Unique Codes', value: stats.total_codes.toLocaleString(), icon: Hash },
    { label: 'Room Templates', value: stats.room_templates, icon: Home },
    { label: 'Always-Write Rules', value: stats.always_write_rules.toLocaleString(), icon: Layers },
  ];

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-lg bg-primary/10 p-3">
                <stat.icon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Corpus Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-sm text-muted-foreground">Co-occurrence Rules</p>
              <p className="text-lg font-semibold">{stats.co_occurrences.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Quantity Relationships</p>
              <p className="text-lg font-semibold">{stats.quantity_relationships.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Data Loaded</p>
              <p className="text-lg font-semibold">{stats.data_loaded ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ================================================================= //
//          Top Codes Tab                                               //
// ================================================================= //

const TopCodesTab: React.FC<{ codes: CodeProfile[] }> = ({ codes }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">Most Frequent Xactimate Codes</CardTitle>
      <CardDescription>Top 20 codes by occurrence across the estimate corpus.</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-slate-50 dark:bg-muted text-left">
              <th className="px-4 py-3 font-medium text-muted-foreground">Code</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Description</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Category</th>
              <th className="px-4 py-3 font-medium text-muted-foreground">Unit</th>
              <th className="px-4 py-3 font-medium text-muted-foreground text-right">Occurrences</th>
              <th className="px-4 py-3 font-medium text-muted-foreground text-right">Avg Qty</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => (
              <tr key={code.xactimate_code} className="border-b hover:bg-slate-50 dark:hover:bg-muted">
                <td className="px-4 py-3 font-mono text-xs text-primary">{code.xactimate_code}</td>
                <td className="px-4 py-3 text-foreground">{code.primary_description}</td>
                <td className="px-4 py-3 text-muted-foreground">{code.category}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{code.primary_unit}</td>
                <td className="px-4 py-3 text-right">
                  <FrequencyBar frequency={code.occurrence_count} max={codes[0]?.occurrence_count || 1} />
                </td>
                <td className="px-4 py-3 text-right font-mono text-foreground">
                  {code.avg_quantity != null ? code.avg_quantity.toFixed(1) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {codes.length === 0 && (
        <p className="py-8 text-center text-muted-foreground">No code data available yet.</p>
      )}
    </CardContent>
  </Card>
);

const FrequencyBar: React.FC<{ frequency: number; max: number }> = ({ frequency, max }) => {
  const pct = max > 0 ? (frequency / max) * 100 : 0;
  return (
    <div className="flex items-center justify-end gap-2">
      <span className="text-xs text-muted-foreground">{frequency}</span>
      <div className="h-2 w-16 rounded-full bg-slate-200 dark:bg-muted">
        <div
          className="h-2 rounded-full bg-primary"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ================================================================= //
//          Room Templates Tab                                         //
// ================================================================= //

const RoomTemplatesTab: React.FC<{ roomTypes: RoomTypeInfo[] }> = ({ roomTypes }) => {
  const [selectedRoom, setSelectedRoom] = useState('');
  const [template, setTemplate] = useState<RoomTemplate | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const handleRoomSelect = async (roomType: string) => {
    setSelectedRoom(roomType);
    if (!roomType) {
      setTemplate(null);
      return;
    }
    setLoadingTemplate(true);
    try {
      const result = await BackendAPI.getRoomTemplate(roomType);
      setTemplate(result);
    } catch {
      setTemplate(null);
    } finally {
      setLoadingTemplate(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Room type selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Room Templates</CardTitle>
          <CardDescription>
            Select a room type to see common line items and patterns.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {roomTypes.map((rt) => (
              <button
                key={rt.room_type}
                onClick={() => handleRoomSelect(rt.room_type)}
                className={cn(
                  'rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
                  selectedRoom === rt.room_type
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'bg-card hover:bg-slate-50 dark:hover:bg-muted'
                )}
              >
                {rt.room_type}
                <span className="ml-1 text-xs text-muted-foreground">({rt.template_count})</span>
              </button>
            ))}
          </div>
          {roomTypes.length === 0 && (
            <p className="py-4 text-center text-muted-foreground">No room types available yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Template display */}
      {loadingTemplate && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {template && !loadingTemplate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {template.room_type} Template
            </CardTitle>
            <CardDescription>
              {template.item_count} common items from {template.sample_count} estimates.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 dark:bg-muted text-left">
                    <th className="px-4 py-3 font-medium text-muted-foreground">Code</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Description</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Activity</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Unit</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-right">Avg Qty</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-right">Freq</th>
                  </tr>
                </thead>
                <tbody>
                  {template.items.map((item, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50 dark:hover:bg-muted">
                      <td className="px-4 py-3 font-mono text-xs text-primary">{item.xactimate_code}</td>
                      <td className="px-4 py-3 text-foreground">{item.description}</td>
                      <td className="px-4 py-3 text-muted-foreground">{item.activity}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{item.unit}</td>
                      <td className="px-4 py-3 text-right font-mono text-foreground">
                        {item.avg_quantity != null ? item.avg_quantity.toFixed(1) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{(item.frequency * 100).toFixed(0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// CodeLookupTab is imported from ../components/AnalysisCodeLookup
